import { Fragment, useMemo } from "react";
import { dayKeyOf, shiftDayKey } from "../lib/days";
import { href, routeQuery } from "../lib/route";
import { clientQuery } from "../lib/clientTabs";
import { useQuerySort } from "../lib/useQuerySort";
import type { Measurement } from "../lib/bodyMetrics";
import type { WindowCount } from "../lib/client";
import { matrix } from "../lib/coverage";
import { recognisedGoals, requirementReadings, resolveStoredGoals, standardFor, weekIsEmpty } from "../lib/goalGaps";
import {
  biggestMoveTile, bodyweightTile, coverageStrip, goalRows, liftRows, LIFTS_SHOWN, progressAddress, progressQuery,
  sessionsTile, setsTile, stripSegments, visibleLifts, type ProgressAddress, type Tile,
} from "../lib/progressPage";
import { useProgressHistory } from "./useProgressHistory";
import { ENGINE_RULE, type OneRMRule } from "../lib/oneRepMax";
import { useCoverageWeek } from "./useCoverageWeek";
import { ProgressChart, ProgressMiniature } from "../components/ProgressChart";
import { BodyMetricsPanel } from "../components/BodyMetricsPanel";
import { ProgressPhotoCard } from "../components/ProgressPhotoCard";
import { ClientCoverage } from "./ClientCoverage";
import { ClientGoals } from "./ClientGoals";

/* 🔴 W72 STEP 4 — PROGRESS, ONE TAB FOR "HOW IS IT GOING" (Proposal 4, his yes; his chart note 2026-09-25 ~01:40).
 * Four tiles · LIFTS AND HOLDS as rows (name + the latest day's dose + when · the app chart's MINIATURE · the last
 * value · the change since the first day), the first six and `All N ›`, the FULL chart opening on its row · GOALS as
 * rows · the COVERAGE strip with its four counts and `Open ›` to the full map (unchanged). The weigh-in chart and the
 * photos open on the bodyweight tile. Every figure is `lib/progressPage.ts`'s; every chart number is the proven twin's.
 * ⚠️ REDESIGNED AWAY (step 2 parked them here): the Training counters, `Last workout`, the Body-metrics card and the
 * Updates feed — the tiles, the week calendar and the lift rows answer the same questions. Rows on the ground, the
 * accent as a line, one sweep: none (the page has no single live thing; the open chart is a surface, not a signal). */

export function ClientProgress({ clientId, today, goals, usesFullOptimization, measurements, measurementsError, last30, firstName }: {
  clientId: string; today: Date; goals: readonly string[] | null; usesFullOptimization: boolean;
  measurements: Measurement[]; measurementsError: string | null; last30: WindowCount; firstName: string | null;
}) {
  const [addr, setAddr] = useQuerySort<ProgressAddress>(progressAddress, routeQuery);
  const go = (next: Omit<ProgressAddress, "fieldMenu">) => {
    setAddr({ ...next, fieldMenu: false });
    window.history.replaceState(null, "", `${href({ name: "client", clientId })}${clientQuery("progress", progressQuery(next))}`);
  };
  const history = useProgressHistory(clientId);
  const week = useCoverageWeek(clientId, shiftDayKey(today, -6), dayKeyOf(today));
  const now = today.getTime();

  const views = (id: string) => (history.status === "ready" ? history.library.get(id)?.progressViews ?? [] : []);
  // W125 — each lift's `1RM` rule (a coach row's switch; an engine exercise keeps today's chart) and the client's weigh-ins.
  const rules = (id: string): OneRMRule => (history.status === "ready" ? history.library.get(id)?.oneRM ?? ENGINE_RULE : ENGINE_RULE);
  const weighIns = history.status === "ready" ? history.weighIns : [];
  // W136 B2 S3r5 P2 — the lift's distance field is `distance_km`: its distance views and lines say km.
  const km = (id: string): boolean => (history.status === "ready" ? history.library.get(id)?.distanceKm ?? false : false);

  const side = useMemo(() => {
    if (week.status !== "ready") return null;
    /* 🔴 THE STRIP READS EVERY CELL, AS THE MAP'S OWN SUMMARY LINE DOES (`coverage.summarise` over `matrix`): it first
       counted only the cells the map's TABLE shows (the audit set), and the same page then printed two different
       "not yet known" figures one click apart — found by rendering the before/after pair. */
    const readings = matrix(week.input);
    const live = recognisedGoals(resolveStoredGoals(goals ?? []));
    const { readings: req } = requirementReadings(live, standardFor(usesFullOptimization), week.input);
    /* ⚠️ AN EMPTY WEEK IS NOT A WEEK THAT TRAINS NOTHING (`goalGaps.weekIsEmpty`, the goal report's own reader). Found
       rendering a fresh client: with nothing assigned every cell read `zeroAccidental` and the strip printed 79 accent
       "gaps" — an indictment of a programme that does not exist. Said as what it is instead. */
    return { strip: coverageStrip(readings), goals: goalRows(live, req), empty: weekIsEmpty(week.input) };
  }, [week, goals, usesFullOptimization]);

  const tiles: Tile[] = [
    bodyweightTile(measurements, today),
    sessionsTile(last30),
    history.status === "ready" ? setsTile(history.histories, now) : { key: "sets", label: "Working sets · 30 days", value: "…", unit: "", sub: "" },
    history.status === "ready" ? biggestMoveTile(history.histories, views, rules, weighIns, km) : { key: "move", label: "Biggest move", value: "…", unit: "", sub: "" },
  ];
  const rows = history.status === "ready" ? liftRows(history.histories, views, rules, weighIns, km) : [];
  const shownRows = visibleLifts(rows, addr.allLifts, addr.lift);

  return (
    <div className="progress-tab">
      <ul className="ptiles" aria-label="How it is going">
        {tiles.map((t) => {
          const body = (
            <>
              <span className="ptile-label">{t.label}</span>
              <span className="ptile-value">{t.value}{t.unit !== "" && <span className="ptile-unit"> {t.unit}</span>}</span>
              <span className="ptile-sub">{t.sub}</span>
            </>
          );
          return (
            <li key={t.key} className="ptile">
              {t.key === "bodyweight" ? (
                <button type="button" className="ptile-open" aria-expanded={addr.open === "body"}
                        onClick={() => go({ ...addr, open: addr.open === "body" ? null : "body" })}>
                  {body}
                  <span className="ptile-more">{addr.open === "body" ? "Close" : "Weigh-ins and photos ›"}</span>
                </button>
              ) : body}
            </li>
          );
        })}
      </ul>
      {measurementsError !== null && <p className="ht-caption pnote" role="alert">Could not read their measurements: {measurementsError}</p>}
      {addr.open === "body" && (
        <section className="pbody" aria-label="Weigh-ins and photos">
          <BodyMetricsPanel clientId={clientId} />
          <ProgressPhotoCard clientId={clientId} firstName={firstName} />
        </section>
      )}

      <div className="pgrid">
        <section className="plifts" aria-labelledby="plifts-h">
          <h2 id="plifts-h" className="pheading">Lifts and holds</h2>
          {history.status === "loading" && <p className="ht-caption pnote" aria-live="polite">Reading their training…</p>}
          {history.status === "failed" && <p className="ht-caption pnote" role="alert">{history.message}</p>}
          {history.status === "ready" && history.notice !== null && <p className="ht-caption pnote" role="alert">{history.notice}</p>}
          {history.status === "ready" && rows.length === 0 && (
            <p className="pempty">Nothing logged yet. Every set they record in the app lands here, lift by lift.</p>
          )}
          <ul className="plift-list">
            {shownRows.map((r) => {
              const h = history.status === "ready" ? history.histories.find((x) => x.id === r.id) : undefined;
              const open = addr.lift === r.id;
              return (
                <Fragment key={r.id}>
                  <li className={`plift${open ? " is-open" : ""}`}>
                    <button type="button" className="plift-row" aria-expanded={open}
                            onClick={() => go({ ...addr, lift: open ? null : r.id })}>
                      <span className="plift-text">
                        <span className="plift-name">{r.name}</span>
                        <span className="plift-dose">{r.dose}{r.lastDone !== "" && <span className="plift-when"> · {r.lastDone}</span>}</span>
                      </span>
                      {h !== undefined && <ProgressMiniature history={h} progressViews={views(r.id)} oneRM={rules(r.id)} weighIns={weighIns} />}
                      <span className="plift-last">{r.last}</span>
                      <span className={`plift-change${r.rising ? " is-up" : ""}`}>{r.change ?? "—"}</span>
                    </button>
                  </li>
                  {open && h !== undefined && history.status === "ready" && (
                    <li className="plift-chart">
                      <ProgressChart history={h} progressViews={views(r.id)} now={now} oneRM={rules(r.id)} weighIns={weighIns} distanceKm={km(r.id)}
                                     testedMaxClientId={clientId}
                                     demoVideoUrl={history.library.get(r.id)?.demoVideoUrl ?? null}
                                     thumbnailUrl={history.library.get(r.id)?.thumbnailUrl ?? null}
                                     notesByInstance={history.notesByInstance}
                                     clipsByInstance={history.clipsByInstance}
                                     initialFieldMenu={addr.fieldMenu}
                                     onClose={() => go({ ...addr, lift: null })} />
                    </li>
                  )}
                </Fragment>
              );
            })}
          </ul>
          {rows.length > LIFTS_SHOWN && (
            <button type="button" className="pmore" onClick={() => go({ ...addr, allLifts: !addr.allLifts })}>
              {addr.allLifts ? "First six" : `All ${rows.length} ›`}
            </button>
          )}
        </section>

        <aside className="pside">
          <section aria-labelledby="pgoals-h">
            <div className="pside-head">
              <h2 id="pgoals-h" className="pheading">Goals</h2>
              <button type="button" className="pmore" aria-expanded={addr.open === "goals"}
                      onClick={() => go({ ...addr, open: addr.open === "goals" ? null : "goals" })}>
                {addr.open === "goals" ? "Close" : "Open ›"}
              </button>
            </div>
            {goals === null && <p className="pempty">This app cannot read their goals.</p>}
            {side !== null && side.empty && goals !== null && (
              <p className="pempty">Nothing is assigned in the last seven days, so no goal can be read against a week.</p>
            )}
            {goals !== null && side !== null && !side.empty && side.goals.length === 0 && (
              <p className="pempty">No goal of theirs names a range this app measures.</p>
            )}
            {week.status === "error" && <p className="ht-caption pnote" role="alert">{week.message}</p>}
            <ol className="pgoal-list">
              {(side !== null && !side.empty ? side.goals : []).map((g, i) => (
                <li key={g.goal} className="pgoal">
                  <span className="pgoal-rank">{i + 1}</span>
                  <span className="pgoal-text">
                    <span className="pgoal-name">{g.goal}</span>
                    <span className="pgoal-standing">{g.standing}</span>
                    <span className="pgoal-bar" aria-hidden="true"><span style={{ width: `${(g.met / g.required) * 100}%` }} /></span>
                    {g.truth !== null && <span className="pgoal-truth">{g.truth}</span>}
                  </span>
                </li>
              ))}
            </ol>
          </section>

          <section aria-labelledby="pcov-h" className="pcoverage">
            <div className="pside-head">
              <h2 id="pcov-h" className="pheading">Coverage · this week</h2>
              <button type="button" className="pmore" aria-expanded={addr.open === "coverage"}
                      onClick={() => go({ ...addr, open: addr.open === "coverage" ? null : "coverage" })}>
                {addr.open === "coverage" ? "Close" : "Open ›"}
              </button>
            </div>
            {side !== null && side.empty && (
              <p className="pempty">Nothing is assigned in the last seven days — there is no week to read.</p>
            )}
            {side !== null && !side.empty && (
              <>
                <span className="pbar" role="img"
                      aria-label={`${side.strip.covered} covered, ${side.strip.thin} thin, ${side.strip.gaps} gaps, ${side.strip.notYetKnown} not yet known`}>
                  {stripSegments(side.strip).map((g) => (
                    <span key={g.tone} className={`pbar-seg is-${g.tone}`} style={{ flexGrow: g.count }} />
                  ))}
                </span>
                <ul className="pstrip-counts">
                  <li><strong>{side.strip.covered}</strong> covered</li>
                  <li><strong>{side.strip.thin}</strong> thin</li>
                  {/* The accent only on a count that needs action (rule 2) — zero gaps needs none. */}
                  <li className={side.strip.gaps > 0 ? "is-gap" : undefined}><strong>{side.strip.gaps}</strong> gaps</li>
                  <li className="is-unknown"><strong>{side.strip.notYetKnown}</strong> not yet known</li>
                </ul>
              </>
            )}
          </section>
        </aside>
      </div>

      {addr.open === "goals" && (
        <ClientGoals clientId={clientId} today={today} goals={goals} usesFullOptimization={usesFullOptimization} />
      )}
      {addr.open === "coverage" && <ClientCoverage clientId={clientId} today={today} />}
    </div>
  );
}
